import React, {
    useRef,
    InputHTMLAttributes,
    ChangeEvent,
    DragEvent,
    SyntheticEvent,
    ReactNode, FC
} from 'react';
import { humanFileSize, validateFile } from '../../utils';

type DragAndDropZoneProps = 
    Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'multiple' | 'type' | 'accept' | 'onChange'> & {
        files: File[];
        accept?: string;
        maxSize?: number;
        maxNumberOfFiles?: number;
        children?: ReactNode;
        onChange: (files: File[]) => void;
    };

const theme = {
    zoneActiveColors: ['border-blue-400', 'ring-4', 'ring-inset'],
    dragged: ['border-blue-600'],
    dragOverColor: ['bg-blue-200', 'bg-opacity-80'],
};

const loadFile: (file: File) => string = (file) => {
    // const preview = document.querySelectorAll('.preview');

    // preview.forEach(elem => {
    //     elem.onload = () => {
    //         URL.revokeObjectURL(elem.src); // free memory
    //     };
    // });

    return URL.createObjectURL(file);
};

const DragAndDropZone: FC<DragAndDropZoneProps> = 
({
     children,
     files,
     onChange,
     accept = '*',
     maxSize = 0,
     maxNumberOfFiles = 0,
     ...rest
}: DragAndDropZoneProps) => {
    const zoneRef = useRef<HTMLDivElement | null>(null);
    const dragIndexRef = useRef<number | null>(null);
    
    const handleChange: (event: ChangeEvent<HTMLInputElement>) => void = (event) => {
        const list = Array.from(event.target.files || [])
            .filter(file =>
                validateFile(accept, file)
                && (maxSize <= 0 || file.size <= maxSize)
            );
        
        const totalQuantity: number = files.length + list.length;
        
        if (maxNumberOfFiles > 0 && totalQuantity > maxNumberOfFiles) {
            onChange([...files, ...list].slice(totalQuantity - maxNumberOfFiles));
        }
        else {
            onChange([...files, ...list]);
        }
    };

    const handleDragOver = () => {
        if (!zoneRef.current) {
            return;
        }
        
        zoneRef.current.classList.add(...theme.zoneActiveColors);
    };

    const handleDragLeave = () => {
        if (!zoneRef.current) {
            return;
        }
        
        zoneRef.current.classList.remove(...theme.zoneActiveColors);
    };
    
    const handleFileDrop: (event: DragEvent<HTMLDivElement>) => void = (event) => {
        const $currentElement = event.target as HTMLDivElement;
        const $el = $currentElement.closest<HTMLDivElement>('[draggable][data-index]');
        
        $currentElement.classList.remove(...theme.dragOverColor);
        
        if (!$el) {
            return;
        }
        
        const { index } = $el.dataset;
        const currentIndex = undefined !== index ? +index : null;
        
        if (null === dragIndexRef.current || null === currentIndex) {
            return;
        }
        
        const list = [ ...files ];

        const removedFiles = list.splice(dragIndexRef.current, 1);
        list.splice(currentIndex, 0, ...removedFiles);
        
        onChange([ ...list ]);
        
        dragIndexRef.current = null;
    };
    
    const handleFileDragOver: (event: DragEvent<HTMLDivElement>) => void = (event) => {
        event.preventDefault();
        
        event.dataTransfer.dropEffect = 'move';
    };
    
    const handleFileDragStart: (event: DragEvent<HTMLDivElement>) => void = (event) => {
        const { index } = event.currentTarget.dataset;
        const currentIndex = undefined !== index ? +index : null;
        
        event.currentTarget.classList.add(...theme.dragged);
        
        dragIndexRef.current = currentIndex;
        
        event.dataTransfer.effectAllowed = 'move';
    };
    
    const handleFileDragEnd: (event: DragEvent<HTMLDivElement>) => void = (event) => {
        event.currentTarget.classList.remove(...theme.dragged);
    };
    
    const handleFileDragEnter: (event: DragEvent<HTMLDivElement>) => void = (event) => {
        const { index } = (event.currentTarget.closest('[draggable][data-index]') as HTMLDivElement).dataset;
        const currentIndex = undefined !== index ? +index : null;
        
        if (dragIndexRef.current !== currentIndex) {
            event.currentTarget.classList.add(...theme.dragOverColor);
        }
    };
    
    const handleFileDragLeave: (event: DragEvent<HTMLDivElement>) => void = (event) => {
        event.currentTarget.classList.remove(...theme.dragOverColor);
    };
    
    const handleRemoveFile: (index: number) => void = (index) => {
        onChange([...files.filter((file, fileIndex) => fileIndex !== index)]);
    };
    
    const handleImageLoad: (event: SyntheticEvent<HTMLImageElement>) => void =
        (event) => {
            URL.revokeObjectURL(event.currentTarget.src);
        };
    
    return (
        <>
            <div
                ref={zoneRef}
                className="relative flex flex-col text-gray-400 border-2 border-gray-200 border-dashed rounded
                 cursor-pointer"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDragLeave}
            >
                <input
                    {...rest}
                    onChange={handleChange}
                    accept={accept}
                    title=""
                    type="file"
                    multiple
                    className="absolute inset-0 z-50 w-full h-full p-0 m-0 outline-none opacity-0 cursor-pointer"
                />

                <div className="flex flex-col items-center justify-center py-10 text-center">
                    {children && children}
                    
                    {!children && (
                        <>
                            <svg
                                className="w-6 h-6 mr-1 text-current-50"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 
                            0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            <p className="m-0">Drag your files here or click in this area.</p>
                        </>
                    )}
                </div>
            </div>

            {files.length !== 0 && (
                <div
                    className="grid grid-cols-2 gap-4 mt-4 md:grid-cols-6"
                    onDrop={handleFileDrop}
                    onDragOver={handleFileDragOver}
                >
                    {files.map((file, index) => (
                        <div
                            key={index}
                            className="relative flex flex-col items-center overflow-hidden text-center 
                            bg-gray-100 border rounded cursor-move select-none"
                            style={{paddingTop: '100%'}}
                            draggable="true"
                            onDragStart={handleFileDragStart}
                            onDragEnd={handleFileDragEnd}
                            data-index={index}
                        >
                            <button
                                className="absolute top-0 right-0 z-50 p-1 bg-white rounded-bl focus:outline-none"
                                type="button"
                                onClick={() => handleRemoveFile(index)}
                            >
                                <svg
                                    className="w-4 h-4 text-gray-700"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 
                                        7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                            </button>

                            {file.type.includes('audio/') && (
                                <svg
                                    className="absolute w-12 h-12 text-gray-400 transform top-1/2 -translate-y-2/3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 
                                        1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 
                                        1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                                    />
                                </svg>
                            )}

                            {file.type.includes('application/') || file.type === '' && (
                                <svg
                                    className="absolute w-12 h-12 text-gray-400 transform top-1/2 -translate-y-2/3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 
                                        1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                    />
                                </svg>
                            )}

                            {file.type.includes('image/') && (
                                <img
                                    className="absolute inset-0 z-0 object-cover w-full h-full 
                                    border-4 border-white preview"
                                    onLoad={handleImageLoad}
                                    src={loadFile(file)}
                                />
                            )}

                            {file.type.includes('video/') && (
                                <video
                                    className="absolute inset-0 object-cover w-full h-full border-4 border-white 
                                    pointer-events-none preview"
                                >
                                    <source
                                        src={loadFile(file)}
                                        type="video/mp4"
                                    />
                                </video>
                            )}

                            <div
                                className="absolute bottom-0 left-0 right-0 flex flex-col p-2 
                                text-xs bg-white bg-opacity-50"
                            >
                                <span className="w-full font-bold text-gray-900 truncate">
                                    {`${file.name || 'Loading'}`}
                                </span>
                                <span className="text-xs text-gray-900">
                                    {`${humanFileSize(file.size) || '...'}`}
                                </span>
                            </div>

                            <div
                                className="absolute inset-0 z-40 transition-colors duration-300"
                                onDragEnter={handleFileDragEnter}
                                onDragLeave={handleFileDragLeave}
                            />
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default DragAndDropZone;